import { useState } from 'react';
import type { ChangeEvent } from 'react';
import type { ConnectionWizardCommonProps } from '../connectionTypes';
import { __ } from '@/hub-shared/utils/i18n';
import { fetchWithNonce } from '../utils/fetchWithNonce';
import { parseApiError } from '../utils/parseApiError';

type ApiKeyWizardProps = ConnectionWizardCommonProps & {
  connected: boolean;
};

const ApiKeyWizard = ({
  restBasePath,
  nonce,
  apiBaseUrl,
  onClose,
  onConnectionUpdated,
  onSessionExpired,
  connected,
}: ApiKeyWizardProps) => {
  const [apiKey, setApiKey] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleApiKeyChange = (event: ChangeEvent<HTMLInputElement>) => {
    setApiKey(event.target.value);
  };

  const handleConnect = async () => {
    if (apiKey.trim() === '') {
      setError(__('Clé API requise.'));
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetchWithNonce(`${apiBaseUrl}${restBasePath}/connect`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': nonce,
        },
        body: JSON.stringify({ apiKey }),
      });

      if (response.status === 401) {
        onSessionExpired();
        return;
      }

      if (response.status === 403) {
        setError(await parseApiError(response));
        return;
      }

      if (!response.ok) {
        setError(await parseApiError(response));
        return;
      }

      setApiKey('');
      onConnectionUpdated();
      onClose();
    } catch {
      setError(__('Impossible de connecter cette API.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDisconnect = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetchWithNonce(`${apiBaseUrl}${restBasePath}/disconnect`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': nonce,
        },
        body: JSON.stringify({}),
      });

      if (response.status === 401) {
        onSessionExpired();
        return;
      }

      if (response.status === 403) {
        setError(await parseApiError(response));
        return;
      }

      if (!response.ok) {
        setError(await parseApiError(response));
        return;
      }

      onConnectionUpdated();
      onClose();
    } catch {
      setError(__('Impossible de déconnecter cette API.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="connection-wizard-content">
      <label htmlFor="api-key-input">{__('Clé API')}</label>
      <input
        id="api-key-input"
        type="password"
        value={apiKey}
        onChange={handleApiKeyChange}
        autoComplete="off"
      />
      <div className="connection-wizard-actions">
        <button type="button" className="btn-secondary" onClick={() => void handleConnect()} disabled={isSubmitting}>
          {connected ? __('Reconnecter') : __('Connecter')}
        </button>
        {connected ? (
          <button type="button" className="btn-secondary" onClick={() => void handleDisconnect()} disabled={isSubmitting}>
            {__('Déconnecter')}
          </button>
        ) : null}
      </div>
      {error ? <p className="connection-wizard-error">{error}</p> : null}
    </div>
  );
};

export default ApiKeyWizard;
